import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import FullModal from "@ats/src/components/modals/FullModal";
import Button from "@ats/src/components/shared/Button";
import Text from "@shared/components/Text";
import LoadingIndicator from "@ats/src/components/shared/LoadingIndicator";
import EmptyState from "@ats/src/components/shared/EmptyState";
import FormContainer from "@ats/src/components/forms/FormContainer";
import CompactFormSection from "@ats/src/components/forms/CompactFormSection";
import FormRadioGroup from "@ats/src/components/forms/FormRadioGroup";

import FitFilterCheckboxes from "@ats/src/views/jobApplications/components/FitFilterCheckboxes";
import CreatedViaFilterCheckboxes from "@ats/src/views/jobApplications/components/CreatedViaFilterCheckboxes";
import SourceFilterSelect from "@ats/src/views/jobApplications/components/SourceFilterSelect";
import KnockoutQuestionFilterRow from "@ats/src/views/jobApplications/components/KnockoutQuestionFilterRow";
import AddQuestionFilterMenu from "@ats/src/views/jobApplications/components/AddQuestionFilterMenu";
import AddSourceFilterMenu from "@ats/src/views/jobApplications/components/AddSourceFilterMenu";
import { FeatureFlipper } from "@ats/src/components/shared/FeatureFlipper";

import { useJobFilters } from "@shared/queryHooks/useJobFilters";
import { ActiveHiringStageFilters } from "@shared/hooks/useHiringStageFilters";
import { convertFilterOptionsToSelectOptions } from "@shared/lib/utils";
import { KnockoutQuestionFilter } from "@shared/types/jobFilters";

export interface JobApplicationFiltersModalProps {
  jobId: number;
  hiringStageId: number;
  stageName?: string;
  activeFilters: ActiveHiringStageFilters;
  onDone: (draftModalFilters: ActiveHiringStageFilters) => void;
  onCancel: (draftModalFilters: ActiveHiringStageFilters) => void;
}

const emptyDraft = (): ActiveHiringStageFilters => ({
  activeRoleFitFilters: new Set<string>(),
  activeKnockoutPassFilters: new Set<string>(),
  activeKnockoutFailFilters: new Set<string>(),
  activeKnockoutNoReplyFilters: new Set<string>(),
  activeSourceFilters: new Set<string>(),
  activeHasResumeFilter: null,
  activeCreatedViaFilters: new Set<string>(),
});

const conditionKeys = {
  pass: "activeKnockoutPassFilters",
  fail: "activeKnockoutFailFilters",
  no_reply: "activeKnockoutNoReplyFilters",
};

const formId = "job-application-filters-form";

const filterKey = (knockoutQuestion: { questionIds: number[] }) =>
  knockoutQuestion.questionIds.join(",");

const pruneToLiveKeys = (filters: Set<string>, liveFilterKeys: Set<string>): Set<string> =>
  new Set(Array.from(filters).filter((key) => liveFilterKeys.has(key)));

const JobApplicationFiltersModal: React.FunctionComponent<JobApplicationFiltersModalProps> = ({
  jobId,
  hiringStageId,
  stageName,
  activeFilters,
  onDone,
  onCancel,
}) => {
  // ModalContext freezes this element at openModal, so the modal works off a local
  // draft copied from the prop and never mutates the prop's Sets.
  const [draftModalFilters, setDraftModalFilters] = React.useState<ActiveHiringStageFilters>(() => ({
    activeRoleFitFilters: new Set(activeFilters.activeRoleFitFilters),
    activeKnockoutPassFilters: new Set(activeFilters.activeKnockoutPassFilters),
    activeKnockoutFailFilters: new Set(activeFilters.activeKnockoutFailFilters),
    activeKnockoutNoReplyFilters: new Set(activeFilters.activeKnockoutNoReplyFilters),
    activeSourceFilters: new Set(activeFilters.activeSourceFilters),
    activeHasResumeFilter: activeFilters.activeHasResumeFilter,
    activeCreatedViaFilters: new Set(activeFilters.activeCreatedViaFilters),
  }));
  const [isDirty, setIsDirty] = React.useState(false);

  const updateDraftModalFilters = (nextDraftModalFilters: ActiveHiringStageFilters) => {
    setDraftModalFilters(nextDraftModalFilters);
    setIsDirty(true);
  };

  // The modal resolves the filters it offers when it opens rather than reading a list
  // the container fetched when the stage loaded.
  const { data: jobFiltersData, isLoading: isLoadingJobFilters } = useJobFilters({ jobId, hiringStageId });
  const knockoutQuestions: Array<KnockoutQuestionFilter> = jobFiltersData?.jobWideFilters.knockoutQuestions;

  // A user can have a filter selected on a question another user has since discarded,
  // un-knocked-out or regrouped. Every fetch of /filters re-derives the set of live keys
  // and drops draft entries that no longer match one.
  React.useEffect(() => {
    if (knockoutQuestions == undefined) return;

    const liveFilterKeys = new Set(
      knockoutQuestions.map((knockoutQuestion) => filterKey(knockoutQuestion)),
    );

    setDraftModalFilters((previousDraft) => {
      const prunedPassFilters = pruneToLiveKeys(
        previousDraft.activeKnockoutPassFilters,
        liveFilterKeys,
      );
      const prunedFailFilters = pruneToLiveKeys(
        previousDraft.activeKnockoutFailFilters,
        liveFilterKeys,
      );
      const prunedNoReplyFilters = pruneToLiveKeys(
        previousDraft.activeKnockoutNoReplyFilters,
        liveFilterKeys,
      );

      if (
        prunedPassFilters.size === previousDraft.activeKnockoutPassFilters.size &&
        prunedFailFilters.size === previousDraft.activeKnockoutFailFilters.size &&
        prunedNoReplyFilters.size === previousDraft.activeKnockoutNoReplyFilters.size
      ) {
        return previousDraft;
      }

      return {
        ...previousDraft,
        activeKnockoutPassFilters: prunedPassFilters,
        activeKnockoutFailFilters: prunedFailFilters,
        activeKnockoutNoReplyFilters: prunedNoReplyFilters,
      };
    });
  }, [knockoutQuestions]);

  const conditionFor = (key: string) => {
    if (draftModalFilters.activeKnockoutPassFilters.has(key)) return "pass";
    if (draftModalFilters.activeKnockoutFailFilters.has(key)) return "fail";
    if (draftModalFilters.activeKnockoutNoReplyFilters.has(key)) return "no_reply";
    return "";
  };

  const activeFilterKeys = (knockoutQuestions || [])
    .map((knockoutQuestion) => filterKey(knockoutQuestion))
    .filter((key) => conditionFor(key) !== "");

  const setCondition = (key: string, condition: string) => {
    const nextDraft = {
      ...draftModalFilters,
      activeKnockoutPassFilters: new Set(draftModalFilters.activeKnockoutPassFilters),
      activeKnockoutFailFilters: new Set(draftModalFilters.activeKnockoutFailFilters),
      activeKnockoutNoReplyFilters: new Set(draftModalFilters.activeKnockoutNoReplyFilters),
    };
    nextDraft.activeKnockoutPassFilters.delete(key);
    nextDraft.activeKnockoutFailFilters.delete(key);
    nextDraft.activeKnockoutNoReplyFilters.delete(key);
    if (condition) {
      nextDraft[conditionKeys[condition]].add(key);
    }
    updateDraftModalFilters(nextDraft);
  };

  const clearKnockoutFilters = () =>
    updateDraftModalFilters({
      ...draftModalFilters,
      activeKnockoutPassFilters: new Set<string>(),
      activeKnockoutFailFilters: new Set<string>(),
      activeKnockoutNoReplyFilters: new Set<string>(),
    });

  const isEmpty =
    draftModalFilters.activeRoleFitFilters.size === 0 &&
    activeFilterKeys.length === 0 &&
    draftModalFilters.activeSourceFilters.size === 0 &&
    draftModalFilters.activeHasResumeFilter == undefined &&
    draftModalFilters.activeCreatedViaFilters.size === 0;

  const handleClearAll = () => {
    updateDraftModalFilters(emptyDraft());
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onDone(draftModalFilters);
  };

  // The section body for Source: a no-options message when the job has no sourced
  // applications (the same treatment knockoutSection gives a job with no knockout
  // questions), otherwise the tag list or its "none added" EmptyState.
  const sourceSection = (sourceOptions: { value: string; label: string }[]) => {
    if (sourceOptions.length === 0) {
      return (
        <EmptyState message="This job has no candidate sources yet. Once a candidate on this job has a source, it can be filtered by here." />
      );
    }

    return (
      <SourceFilterSelect
        options={sourceOptions}
        value={Array.from(draftModalFilters.activeSourceFilters)}
        onChange={(name, value) => updateDraftModalFilters({ ...draftModalFilters, activeSourceFilters: new Set(value) })}
      />
    );
  };

  // Called only from filterContent(), which the render guards on knockoutQuestions, so
  // these derivations never run against an unresolved query.
  const knockoutSection = () => {
    if (knockoutQuestions.length === 0) {
      return (
        <EmptyState message="This job has no knockout questions or no candidates have answered any existing knockout questions. Mark a question as a knockout question on the job's application form to filter by it." />
      );
    }

    const addedKnockoutQuestions = knockoutQuestions.filter(
      (knockoutQuestion) => conditionFor(filterKey(knockoutQuestion)) !== "",
    );

    if (addedKnockoutQuestions.length === 0) {
      return <EmptyState message="No question filters have been added" />;
    }

    return (
      <Styled.QuestionList>
        {addedKnockoutQuestions.map((knockoutQuestion) => {
          const key = filterKey(knockoutQuestion);
          return (
            <KnockoutQuestionFilterRow
              key={key}
              knockoutQuestion={knockoutQuestion}
              condition={conditionFor(key)}
              onConditionChange={(condition) => setCondition(key, condition)}
              onRemove={() => setCondition(key, "")}
            />
          );
        })}
      </Styled.QuestionList>
    );
  };

  const filterContent = () => {
    // Called only from the render branch that guards on knockoutQuestions, so jobFiltersData
    // is resolved here and both groups carry every key the backend serves.
    const sourceOptions = convertFilterOptionsToSelectOptions(jobFiltersData.jobWideFilters.source);
    const hasResumeOptions = convertFilterOptionsToSelectOptions(jobFiltersData.jobWideFilters.hasResume);
    const createdViaOptions = convertFilterOptionsToSelectOptions(jobFiltersData.jobWideFilters.createdVia);

    const hiringStageKnockoutQuestions = jobFiltersData.hiringStageFilters.knockoutQuestions;
    const disabledFilterKeys = knockoutQuestions
      .filter(
        (knockoutQuestion) =>
          !hiringStageKnockoutQuestions.some((hiringStageKnockoutQuestion) =>
            hiringStageKnockoutQuestion.questionIds.some((questionId) =>
              knockoutQuestion.questionIds.includes(questionId),
            ),
          ),
      )
      .map((knockoutQuestion) => filterKey(knockoutQuestion));

    const hiringStageSourceOptions = convertFilterOptionsToSelectOptions(jobFiltersData.hiringStageFilters.source);
    const disabledSourceValues = sourceOptions
      .filter(
        (sourceOption) =>
          !hiringStageSourceOptions.some((hiringStageSourceOption) => hiringStageSourceOption.value === sourceOption.value),
      )
      .map((sourceOption) => sourceOption.value);

    return (
      <FormContainer id={formId} onSubmit={handleSubmit}>
        <FeatureFlipper feature="AI_APPLICANT_SUMMARY">
          <CompactFormSection
            title="Fit"
            qualifier="is any of"
            onClear={() => updateDraftModalFilters({ ...draftModalFilters, activeRoleFitFilters: new Set<string>() })}
            clearDisabled={draftModalFilters.activeRoleFitFilters.size === 0}
          >
            <FitFilterCheckboxes
              activeFilters={draftModalFilters.activeRoleFitFilters}
              onChange={(activeRoleFitFilters) => updateDraftModalFilters({ ...draftModalFilters, activeRoleFitFilters })}
            />
          </CompactFormSection>

          <Styled.Divider />
        </FeatureFlipper>

        <CompactFormSection
          title="Created via"
          qualifier="is any of"
          onClear={() => updateDraftModalFilters({ ...draftModalFilters, activeCreatedViaFilters: new Set<string>() })}
          clearDisabled={draftModalFilters.activeCreatedViaFilters.size === 0}
        >
          <CreatedViaFilterCheckboxes
            options={createdViaOptions}
            activeFilters={draftModalFilters.activeCreatedViaFilters}
            onChange={(activeCreatedViaFilters) => updateDraftModalFilters({ ...draftModalFilters, activeCreatedViaFilters })}
          />
        </CompactFormSection>

        <Styled.Divider />

        <CompactFormSection
          title="Resume"
          qualifier="is one of"
          onClear={() => updateDraftModalFilters({ ...draftModalFilters, activeHasResumeFilter: null })}
          clearDisabled={draftModalFilters.activeHasResumeFilter == undefined}
        >
          {/* The draft holds a boolean; the radio group round-trips strings (FormRadioGroup
            reads event.currentTarget.value), so the value is stringified on the way in and
            compared on the way out. "" matches no option, which is how unset renders. */}
          <FormRadioGroup
            name="resume"
            options={hasResumeOptions}
            value={
              draftModalFilters.activeHasResumeFilter == undefined ? "" : String(draftModalFilters.activeHasResumeFilter)
            }
            onChange={(name, value) =>
              updateDraftModalFilters({ ...draftModalFilters, activeHasResumeFilter: value === "true" })
            }
          />
        </CompactFormSection>

        <Styled.Divider />

        <CompactFormSection
          title="Source"
          qualifier="is any of"
          onClear={() => updateDraftModalFilters({ ...draftModalFilters, activeSourceFilters: new Set<string>() })}
          clearDisabled={draftModalFilters.activeSourceFilters.size === 0}
          footer={
            <AddSourceFilterMenu
              options={sourceOptions}
              activeValues={Array.from(draftModalFilters.activeSourceFilters)}
              disabledValues={disabledSourceValues}
              onSelect={(value) => {
                const activeSourceFilters = new Set(draftModalFilters.activeSourceFilters);
                activeSourceFilters.add(value);
                updateDraftModalFilters({ ...draftModalFilters, activeSourceFilters });
              }}
            />
          }
        >
          {sourceSection(sourceOptions)}
        </CompactFormSection>

        <Styled.Divider />

        <CompactFormSection
          title="Knockout questions"
          qualifier="all must match"
          onClear={clearKnockoutFilters}
          clearDisabled={activeFilterKeys.length === 0}
          footer={
            <AddQuestionFilterMenu
              knockoutQuestions={knockoutQuestions}
              filterKeyFor={filterKey}
              activeFilterKeys={activeFilterKeys}
              disabledFilterKeys={disabledFilterKeys}
              onSelect={(key) => setCondition(key, "pass")}
            />
          }
        >
          {knockoutSection()}
        </CompactFormSection>
      </FormContainer>
    );
  };

  return (
    <FullModal onCancel={() => onCancel(draftModalFilters)} hasUnsavedChanges={isDirty}>
      <Styled.Panel>
        <Styled.Header>
          <Text as="h2" size="xl" weight="bold">
            {stageName ? `${stageName} filters` : "Filter candidates"}
          </Text>
          <Styled.HeaderActions>
            <Button styleType="text" onClick={handleClearAll} disabled={isEmpty}>
              Clear all
            </Button>
          </Styled.HeaderActions>
        </Styled.Header>

        {/* The body holds the panel's height in both states, so the button row lands in the
            same place whether /filters is in flight or resolved and a click aimed at Save
            cannot land on the filter content that mounts under it. */}
        {isLoadingJobFilters || knockoutQuestions == undefined ? (
          <Styled.Loading>
            <LoadingIndicator label="Loading..." />
          </Styled.Loading>
        ) : (
          <Styled.Body>{filterContent()}</Styled.Body>
        )}

        <Styled.Footer>
          <Button type="submit" form={formId} styleType="primary">
            Save filters
          </Button>
          <Button styleType="secondary" onClick={() => onCancel(draftModalFilters)}>
            Cancel
          </Button>
        </Styled.Footer>
      </Styled.Panel>
    </FullModal>
  );
};

export default JobApplicationFiltersModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Panel = styled.div`
  label: JobApplicationFiltersModal_Panel;
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

Styled.Body = styled.div`
  label: JobApplicationFiltersModal_Body;
  flex: 1 0 auto;
`;

Styled.Loading = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationFiltersModal_Loading;
    flex: 1 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;

    > div {
      ${t.m(0)}
    }
  `;
});

Styled.Header = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationFiltersModal_Header;
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: ${t.spacing[4]};
    padding: ${t.spacing[5]} ${t.spacing[5]} ${t.spacing[4]};
    border-bottom: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
    color: ${t.dark ? t.color.gray[200] : t.color.black};
  `;
});

Styled.HeaderActions = styled.div`
  label: JobApplicationFiltersModal_HeaderActions;
  margin-left: auto;
`;

/* same construction as AccountTeam's Styled.Team: the container draws the outer
   border, each row draws its own bottom rule and drops it on the last one */
Styled.QuestionList = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationFiltersModal_QuestionList;
    ${t.rounded.md}
    border: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
  `;
});

Styled.Divider = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationFiltersModal_Divider;
    height: 1px;
    background-color: ${t.dark ? t.color.gray[700] : t.color.gray[200]};
  `;
});

Styled.Footer = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationFiltersModal_Footer;
    position: sticky;
    bottom: 0;
    z-index: 2;
    display: flex;
    gap: ${t.spacing[3]};
    padding: ${t.spacing[4]} ${t.spacing[5]};
    border-top: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    border-bottom-left-radius: 0.4375rem;
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
  `;
});
